import React, { useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ResponsiveContainer, XAxis, YAxis, Tooltip,
  CartesianGrid, Line, LineChart, ReferenceArea, AreaChart, Area,
} from "recharts";
import { ArrowLeft, Activity, Users, Lock, Clock, Hash, Copy, Play, Star, Share2 } from "lucide-react";
import { toast } from "sonner";
import { api, fetchServer, fetchServerHistory, fetchServerDetails, joinServer } from "../lib/api";
import TopNav from "../components/TopNav";
import PingIndicator from "../components/PingIndicator";
import MapThumbnail from "../components/MapThumbnail";
import RegionFlag from "../components/RegionFlag";
import FavoritesSheet from "../components/FavoritesSheet";
import { RichHistoryTooltip } from "../components/RichHistoryTooltip";
import ServerReliabilityPanel from "../components/ServerReliabilityPanel";
import ServerDetailsAccordion from "../components/ServerDetailsAccordion";
import DataAccumulatingNotice from "../components/DataAccumulatingNotice";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { useFavorites } from "../context/FavoritesContext";
import { useGame } from "../context/GameContext";
import { formatHours, formatNumber, lastSeenShort, formatDuration } from "../lib/format";

const fetchDaily = async (id, days = 30) =>
  (await api.get(`/servers/${encodeURIComponent(id)}/daily?days=${days}`)).data;

const Pill = ({ icon, label, value }) => (
  <div
    className="flex items-center gap-2 border px-3 py-2"
    style={{ borderColor: "var(--border-color)", background: "var(--bg-main)", borderRadius: 2 }}
  >
    <span style={{ color: "var(--primary-accent)" }}>{icon}</span>
    <div className="min-w-0">
      <div className="hud-label">{label}</div>
      <div className="font-mono-hud text-sm truncate" style={{ color: "var(--text-primary)" }}>{value}</div>
    </div>
  </div>
);

const ServerPage = () => {
  const { id: serverId } = useParams();
  const navigate = useNavigate();
  const [favOpen, setFavOpen] = React.useState(false);
  const { isFavorite, toggleFavorite } = useFavorites();
  const { setGameId } = useGame();

  const { data: server, isLoading, isError } = useQuery({
    queryKey: ["server", serverId],
    queryFn: () => fetchServer(serverId),
    refetchInterval: 30000,
    retry: 1,
  });
  const [bucket, setBucket] = React.useState(30);
  const [chartWindow, setChartWindow] = React.useState("1d");
  const { data: history } = useQuery({
    queryKey: ["history", serverId, bucket, chartWindow],
    queryFn: () => fetchServerHistory(serverId, bucket, chartWindow),
    staleTime: 0,
  });
  const { data: daily } = useQuery({
    queryKey: ["daily", serverId, 30],
    queryFn: () => fetchDaily(serverId, 30),
    staleTime: 60000,
  });
  const { data: details } = useQuery({
    queryKey: ["details", serverId],
    queryFn: () => fetchServerDetails(serverId),
    refetchInterval: 60000,
    retry: 1,
  });

  useEffect(() => {
    if (server?.game_id) setGameId(server.game_id);
  }, [server?.game_id, setGameId]);

  if (isError && !server) {
    return (
      <div className="min-h-screen">
        <TopNav onOpenFavorites={() => setFavOpen(true)} />
        <main className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6">
          <Link to="/" className="inline-flex items-center gap-2 hud-caption hover:underline mb-6"
            style={{ color: "var(--primary-accent)" }}>
            <ArrowLeft className="w-3.5 h-3.5" /> BACK TO BROWSER
          </Link>
          <div className="tac-card p-10 text-center" style={{ background: "var(--bg-surface)" }} data-testid="server-not-found">
            <span className="corner-tr" /><span className="corner-bl" />
            <div className="hud-caption mb-2" style={{ color: "var(--ping-high)" }}>SERVER NOT FOUND</div>
            <h1 className="font-head text-3xl font-extrabold mb-2" style={{ color: "var(--text-primary)" }}>
              NO TRACE OF <span style={{ color: "var(--primary-accent)" }}>{serverId}</span>
            </h1>
            <p className="text-sm max-w-md mx-auto" style={{ color: "var(--text-secondary)" }}>
              This server id isn't tracked right now. It may have been retired by the operator or the link is stale.
            </p>
          </div>
        </main>
      </div>
    );
  }

  if (isLoading || !server) {
    return (
      <div className="min-h-screen">
        <TopNav onOpenFavorites={() => setFavOpen(true)} />
        <main className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6">
          <Skeleton className="h-10 w-96" style={{ background: "var(--bg-elevated)" }} />
          <div className="grid grid-cols-3 gap-4 mt-6">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-20" style={{ background: "var(--bg-elevated)" }} />)}
          </div>
          <Skeleton className="h-72 mt-6" style={{ background: "var(--bg-elevated)" }} />
        </main>
      </div>
    );
  }

  const fav = isFavorite(server.id);
  const chartHistory = (history?.history || []).map((p) => ({
    t: p.t,
    time: new Date(p.t).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    endTime: new Date(new Date(p.t).getTime() + (history?.bucket_minutes || bucket) * 60000)
      .toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" }),
    players: p.players,
    peak_players: p.peak_players ?? p.players,
    ping: p.ping,
    online: p.online !== false,
    online_pct: p.online_pct ?? (p.online !== false ? 100 : 0),
    map: p.map,
  }));
  const pingSeries = chartHistory.filter((p) => p.online && p.ping > 0);
  const avgPing = pingSeries.length
    ? Math.round(pingSeries.reduce((a, b) => a + b.ping, 0) / pingSeries.length)
    : null;
  const peakPing = pingSeries.length ? Math.max(...pingSeries.map((p) => p.ping)) : null;
  const minPing = pingSeries.length ? Math.min(...pingSeries.map((p) => p.ping)) : null;
  const dailyRows = (daily?.rows || []).map((r) => ({
    date: r.date,
    label: r.date.slice(5),
    peak: r.peak_players,
    avg: r.avg_players,
    uptime: r.uptime_pct,
    flaps: r.flap_count,
  }));
  const peak30 = dailyRows.reduce((m, r) => Math.max(m, r.peak), 0);
  const avgUptime = dailyRows.length
    ? Math.round(dailyRows.reduce((s, r) => s + r.uptime, 0) / dailyRows.length)
    : null;
  const totalFlaps = dailyRows.reduce((s, r) => s + r.flaps, 0);

  const handleShare = () => {
    const url = `${window.location.origin}/server/${encodeURIComponent(server.id)}`;
    navigator.clipboard.writeText(url);
    toast.success("Share link copied", { description: url });
  };
  const handleJoin = async () => {
    try {
      const res = await joinServer(server.id);
      const cmd = res.connect_command || "";
      if (cmd.startsWith("wardogs://")) {
        try { window.location.href = cmd; } catch { /* ignore */ }
        navigator.clipboard.writeText(cmd);
        toast.success("Launching Wardogs...", { description: cmd });
      } else {
        navigator.clipboard.writeText(cmd);
        toast.success("Connect command copied", { description: cmd });
      }
    } catch { toast.error("Could not join server"); }
  };

  return (
    <div className="min-h-screen" data-testid="server-page">
      <TopNav onOpenFavorites={() => setFavOpen(true)} />
      <main className="max-w-[1400px] mx-auto px-4 lg:px-8 py-6 space-y-6">
        <Link
          to="/"
          data-testid="back-to-browser"
          className="inline-flex items-center gap-2 hud-caption hover:underline"
          style={{ color: "var(--primary-accent)" }}
        >
          <ArrowLeft className="w-3.5 h-3.5" /> BACK TO BROWSER
        </Link>

        {/* Header */}
        <div className="flex flex-wrap items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="hud-caption flex items-center gap-2 flex-wrap">
              {server.id}
              <span style={{ color: server.online ? "var(--highlight)" : "var(--ping-high)" }}>
                · {server.online ? "ONLINE" : "OFFLINE"}
              </span>
              {!server.online && server.last_online_at && <span>· LAST SEEN {lastSeenShort(server.last_online_at)}</span>}
              {server.region && (
                <span className="inline-flex items-center gap-1.5" data-testid="header-region">
                  ·
                  <RegionFlag region={server.region} size={14} testId="header-region-flag" />
                  <span className="uppercase">{server.region}</span>
                </span>
              )}
            </div>
            <h1
              className="font-head text-3xl md:text-4xl font-extrabold uppercase mt-1"
              style={{ color: "var(--text-primary)", letterSpacing: "0.02em", lineHeight: 1.15 }}
              data-testid="server-title"
            >
              {server.name}
            </h1>
            <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>{server.description}</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button onClick={handleJoin} data-testid="page-join-btn" className="font-head tracking-widest font-bold"
              style={{ background: "var(--primary-accent)", color: "#000", borderRadius: 2 }}>
              <Play className="w-4 h-4 mr-2" /> JOIN
            </Button>
            <Button variant="outline" onClick={() => toggleFavorite(server)} data-testid="page-fav-btn"
              className="font-mono-hud tracking-widest"
              style={{
                background: fav ? "color-mix(in srgb, var(--secondary-accent) 20%, transparent)" : "var(--bg-elevated)",
                borderColor: fav ? "var(--secondary-accent)" : "var(--border-strong)",
                color: fav ? "var(--secondary-accent)" : "var(--text-primary)",
                borderRadius: 2,
              }}>
              <Star className="w-4 h-4 mr-2" style={{ fill: fav ? "var(--secondary-accent)" : "transparent" }} />
              {fav ? "FAVORITED" : "FAVORITE"}
            </Button>
            <Button variant="outline" onClick={handleShare} data-testid="page-share-btn"
              className="font-mono-hud tracking-widest"
              style={{ background: "var(--bg-elevated)", borderColor: "var(--border-strong)", color: "var(--text-primary)", borderRadius: 2 }}>
              <Share2 className="w-4 h-4 mr-2" /> SHARE
            </Button>
          </div>
        </div>

        {/* Pills + 30-day summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          <Pill icon={<Users className="w-4 h-4" />} label="PLAYERS" value={`${server.players}/${server.max_players}`} />
          <Pill icon={<RegionFlag region={server.region} size={18} />} label="REGION" value={server.region} />
          <Pill icon={<Clock className="w-4 h-4" />} label="UPTIME" value={formatHours(history?.uptime_hours ?? server.uptime_hours)} />
          <Pill icon={<Activity className="w-4 h-4" />} label="PING" value={<PingIndicator ping={server.ping} online={server.online} server={server} />} />
          <Pill icon={<Hash className="w-4 h-4" />} label="30D PEAK" value={peak30 || "—"} />
          <Pill icon={<Clock className="w-4 h-4" />} label="30D UPTIME" value={avgUptime == null ? "—" : `${avgUptime}%`} />
        </div>

        {/* Wardogs map-rotation timings */}
        {server.source === "wardogs" && (history?.current_map_duration_min != null || history?.previous_map_duration_min != null) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="map-rotation-timings">
            <div className="tac-card p-4" style={{ background: "var(--bg-surface)" }}>
              <span className="corner-tr" /><span className="corner-bl" />
              <div className="hud-label">PLAYING NOW · {server.map}</div>
              <div
                className="font-head text-2xl font-extrabold mt-1 tabular-nums"
                style={{ color: "var(--primary-accent)" }}
                data-testid="current-map-duration"
              >
                {history?.current_map_duration_min != null ? formatDuration(history.current_map_duration_min) : "—"}
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--text-dim)" }}>
                In progress
                {history?.current_map_started_at
                  ? ` · started at ${new Date(history.current_map_started_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
                  : ""}
              </div>
            </div>
            <div className="tac-card p-4" style={{ background: "var(--bg-surface)" }}>
              <span className="corner-tr" /><span className="corner-bl" />
              <div className="hud-label">PREVIOUS MAP · LASTED</div>
              <div
                className="font-head text-2xl font-extrabold mt-1 tabular-nums"
                style={{ color: "var(--secondary-accent)" }}
                data-testid="previous-map-duration"
              >
                {history?.previous_map_duration_min != null ? formatDuration(history.previous_map_duration_min) : "—"}
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--text-dim)" }}>
                Before the current rotation flipped
              </div>
            </div>
          </div>
        )}

        {/* Wardogs map preview + modifier chips */}
        {server.source === "wardogs" && (
          <div className="flex flex-col md:flex-row gap-4 items-start tac-card p-4" style={{ background: "var(--bg-surface)" }}>
            <span className="corner-tr" /><span className="corner-bl" />
            <MapThumbnail variant={server.map_variant || server.map} base={server.map_base} width={240} height={135} />
            <div className="flex-1">
              <div className="hud-label mb-2">MODIFIERS</div>
              {server.modifiers?.length ? (
                <div className="flex flex-wrap gap-2">
                  {server.modifiers.map((m) => (
                    <span key={m} className="font-mono-hud text-xs tracking-widest px-2 py-1 border"
                      style={{
                        color: "var(--primary-accent)",
                        borderColor: "color-mix(in srgb, var(--primary-accent) 50%, transparent)",
                        background: "color-mix(in srgb, var(--primary-accent) 12%, transparent)",
                        borderRadius: 2,
                      }}>{m}</span>
                  ))}
                </div>
              ) : (
                <span className="font-mono-hud text-sm" style={{ color: "var(--text-dim)" }}>STANDARD · NO MODIFIERS</span>
              )}
            </div>
          </div>
        )}

        {/* P1 Reliability panel — uptime 7D/30D, match duration, reserved, tracked since */}
        <ServerReliabilityPanel details={details} />

        {/* Player chart with window + resolution toggles */}
        <div className="tac-card p-4" style={{ background: "var(--bg-surface)" }}>
          <span className="corner-tr" /><span className="corner-bl" />
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="hud-label">PLAYER COUNT · {chartWindow === "1d" ? "LAST 24H" : chartWindow === "1w" ? "LAST 7 DAYS" : "LAST 30 DAYS"}</div>
              <div className="font-head text-lg uppercase font-bold" style={{ color: "var(--text-primary)" }}>
                PEAK {history?.peak_players_24h ?? "—"} · AVG {history?.average_players_24h ?? "—"}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1" data-testid="window-toggle">
                <span className="hud-label mr-1">WINDOW</span>
                {[
                  { k: "1d", label: "1D" },
                  { k: "1w", label: "1W" },
                  { k: "1m", label: "1M" },
                ].map((w) => (
                  <button
                    key={w.k}
                    type="button"
                    onClick={() => setChartWindow(w.k)}
                    data-testid={`window-${w.k}`}
                    className="font-mono-hud text-xs tracking-widest px-2 py-1 border transition-colors"
                    style={{
                      color: chartWindow === w.k ? "#000" : "var(--text-primary)",
                      background: chartWindow === w.k ? "var(--highlight)" : "var(--bg-elevated)",
                      borderColor: chartWindow === w.k ? "var(--highlight)" : "var(--border-strong)",
                      borderRadius: 2,
                    }}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
              {chartWindow === "1d" && (
                <div className="flex items-center gap-1" data-testid="bucket-toggle">
                  <span className="hud-label mr-1">RESOLUTION</span>
                  {[10, 30, 60].map((b) => (
                    <button
                      key={b}
                      type="button"
                      onClick={() => setBucket(b)}
                      data-testid={`bucket-${b}`}
                      className="font-mono-hud text-xs tracking-widest px-2 py-1 border transition-colors"
                      style={{
                        color: bucket === b ? "#000" : "var(--text-primary)",
                        background: bucket === b ? "var(--primary-accent)" : "var(--bg-elevated)",
                        borderColor: bucket === b ? "var(--primary-accent)" : "var(--border-strong)",
                        borderRadius: 2,
                      }}
                    >
                      {b < 60 ? `${b}M` : "1H"}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DataAccumulatingNotice window={chartWindow} pointCount={history?.history?.length || 0} testId="player-data-accumulating" />
          <div className="h-[240px]" data-testid="chart-24h">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartHistory} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="pg24" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="var(--primary-accent)" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="var(--primary-accent)" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="var(--border-color)" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="t" stroke="var(--text-dim)" tick={{ fontSize: 10, fontFamily: "var(--font-mono)" }} tickLine={false}
                  tickFormatter={(iso) => chartWindow === "1d"
                    ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
                    : new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                  interval="preserveStartEnd" minTickGap={28} />
                <YAxis stroke="var(--text-dim)" tick={{ fontSize: 10, fontFamily: "var(--font-mono)" }} tickLine={false} width={36} />
                <Tooltip
                  content={<RichHistoryTooltip bucket={history?.bucket_minutes || bucket} metric="players" />}
                  cursor={{ stroke: "var(--primary-accent)", strokeOpacity: 0.4 }}
                />
                <Area type="monotone" dataKey="players" stroke="var(--primary-accent)" strokeWidth={2} fill="url(#pg24)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* P2-P3 Game-specific details accordion (collapsible) */}
        <ServerDetailsAccordion details={details} />

        {/* Ping sub-chart */}
        <div className="tac-card p-4" style={{ background: "var(--bg-surface)" }}>
          <span className="corner-tr" /><span className="corner-bl" />
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <div>
              <div className="hud-label">PING · {chartWindow === "1d" ? "LAST 24H" : chartWindow === "1w" ? "LAST 7 DAYS" : "LAST 30 DAYS"}</div>
              <div className="font-head text-lg uppercase font-bold" style={{ color: "var(--text-primary)" }}>
                MIN {minPing ?? "—"}MS · AVG {avgPing ?? "—"}MS · PEAK <span style={{ color: peakPing && peakPing > 150 ? "var(--ping-high)" : "var(--text-primary)" }}>{peakPing ?? "—"}MS</span>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex items-center gap-1" data-testid="ping-window-toggle">
                <span className="hud-label mr-1">WINDOW</span>
                {[
                  { k: "1d", label: "1D" },
                  { k: "1w", label: "1W" },
                  { k: "1m", label: "1M" },
                ].map((w) => (
                  <button
                    key={w.k}
                    type="button"
                    onClick={() => setChartWindow(w.k)}
                    data-testid={`ping-window-${w.k}`}
                    className="font-mono-hud text-xs tracking-widest px-2 py-1 border transition-colors"
                    style={{
                      color: chartWindow === w.k ? "#000" : "var(--text-primary)",
                      background: chartWindow === w.k ? "var(--highlight)" : "var(--bg-elevated)",
                      borderColor: chartWindow === w.k ? "var(--highlight)" : "var(--border-strong)",
                      borderRadius: 2,
                    }}
                  >
                    {w.label}
                  </button>
                ))}
              </div>
              {chartWindow === "1d" && (
                <div className="flex items-center gap-1" data-testid="ping-bucket-toggle">
                  <span className="hud-label mr-1">RESOLUTION</span>
                  {[10, 30, 60].map((b) => (
                    <button
                      key={b}
                      type="button"
                      onClick={() => setBucket(b)}
                      data-testid={`ping-bucket-${b}`}
                      className="font-mono-hud text-xs tracking-widest px-2 py-1 border transition-colors"
                      style={{
                        color: bucket === b ? "#000" : "var(--text-primary)",
                        background: bucket === b ? "var(--secondary-accent)" : "var(--bg-elevated)",
                        borderColor: bucket === b ? "var(--secondary-accent)" : "var(--border-strong)",
                        borderRadius: 2,
                      }}
                    >
                      {b < 60 ? `${b}M` : "1H"}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DataAccumulatingNotice window={chartWindow} pointCount={history?.history?.length || 0} testId="ping-data-accumulating" />
          <div className="h-[220px]" data-testid="chart-ping">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartHistory} margin={{ top: 5, right: 12, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="var(--border-color)" strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="t" stroke="var(--text-dim)" tick={{ fontSize: 10, fontFamily: "var(--font-mono)" }} tickLine={false}
                  tickFormatter={(iso) => chartWindow === "1d"
                    ? new Date(iso).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })
                    : new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                  interval="preserveStartEnd" minTickGap={28} />
                <YAxis stroke="var(--text-dim)" tick={{ fontSize: 10, fontFamily: "var(--font-mono)" }} tickLine={false} width={40}
                  tickFormatter={(v) => `${v}`} />
                <Tooltip
                  content={<RichHistoryTooltip bucket={history?.bucket_minutes || bucket} metric="ping" />}
                  cursor={{ stroke: "var(--secondary-accent)", strokeOpacity: 0.4 }}
                />
                {/* 150ms "high ping" reference band */}
                <ReferenceArea y1={150} y2={9999} fill="var(--ping-high)" fillOpacity={0.08} ifOverflow="hidden" />
                <Line
                  type="monotone"
                  dataKey="ping"
                  stroke="var(--secondary-accent)"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pro-tier teaser */}
        <div className="tac-card p-5 text-center" style={{ background: "var(--bg-surface)" }}>
          <span className="corner-tr" /><span className="corner-bl" />
          <div className="hud-label mb-1" style={{ color: "var(--official-color)" }}>SERVER-RECON PRO</div>
          <div className="font-head text-lg uppercase font-bold" style={{ color: "var(--text-primary)" }}>
            Need 365-day history, fill-rate heatmaps and alerts?
          </div>
          <p className="text-sm mt-2 max-w-xl mx-auto" style={{ color: "var(--text-secondary)" }}>
            Free plan keeps 30 days per server. Pro unlocks a full year, CSV export,
            server comparison and Discord/email alerts when your favorites fill up.
          </p>
          <div className="mt-3 hud-caption">COMING SOON</div>
        </div>

        <FavoritesSheet open={favOpen} onOpenChange={setFavOpen} onOpenServer={(s) => navigate(`/server/${encodeURIComponent(s.id)}`)} />
      </main>
    </div>
  );
};

export default ServerPage;
